import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Snackbar,
  Alert,
  Pagination,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import {
  ProjectCard,
  ProjectFilters,
  ProjectDetailModal,
} from '../../components/projects';
import { useProjects } from '../../hooks/useProjects';
import { useProjectMutations } from '../../hooks/useProjectMutations';
import { useClients } from '../../hooks/useClients';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import type {
  ProjectFilters as ProjectFiltersType,
  ProjectWithRelations,
  CreateProjectRequest,
  UpdateProjectRequest,
} from '../../types/project';

export const ProjectsPage: React.FC = () => {
  // Filters state
  const [filters, setFilters] = useState<ProjectFiltersType>({
    page: 1,
    limit: 12,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  // Modal states
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectWithRelations | null>(null);
  const [projectToDelete, setProjectToDelete] = useState<ProjectWithRelations | null>(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Data fetching
  const { data, isLoading } = useProjects(filters);
  const mutations = useProjectMutations();
  const { data: clientsData } = useClients();

  const projects = data?.data?.projects || [];
  const pagination = data?.data?.pagination;
  const clients = clientsData?.clients || [];

  // Handlers
  const handleFiltersChange = useCallback((newFilters: ProjectFiltersType) => {
    setFilters(newFilters);
  }, []);

  const handlePageChange = useCallback((_: React.ChangeEvent<unknown>, page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const handleOpenNewProject = () => {
    setSelectedProject(null);
    setIsDetailModalOpen(true);
  };

  const handleEditProject = (project: ProjectWithRelations) => {
    setSelectedProject(project);
    setIsDetailModalOpen(true);
  };

  const handleSaveProject = async (data: CreateProjectRequest | UpdateProjectRequest) => {
    try {
      if (selectedProject) {
        await mutations.updateProject.mutateAsync({
          id: selectedProject._id,
          data: data as UpdateProjectRequest,
        });
        setSnackbar({
          open: true,
          message: 'Project updated successfully',
          severity: 'success',
        });
      } else {
        await mutations.createProject.mutateAsync(data as CreateProjectRequest);
        setSnackbar({
          open: true,
          message: 'Project created successfully',
          severity: 'success',
        });
      }
      setIsDetailModalOpen(false);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to save project',
        severity: 'error',
      });
      throw error;
    }
  };

  const handleDeleteClick = (project: ProjectWithRelations) => {
    setProjectToDelete(project);
  };

  const handleConfirmDelete = async () => {
    if (!projectToDelete) return;

    try {
      await mutations.deleteProject.mutateAsync(projectToDelete._id);
      setSnackbar({
        open: true,
        message: 'Project deleted successfully',
        severity: 'success',
      });
      setProjectToDelete(null);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to delete project',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Empty state
  const EmptyState = () => (
    <Paper
      sx={{
        textAlign: 'center',
        py: 8,
        px: 4,
        bgcolor: 'background.paper',
      }}
    >
      <FolderIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        No projects yet
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Create your first project to organize tasks and notes
      </Typography>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleOpenNewProject}
      >
        Create Your First Project
      </Button>
    </Paper>
  );

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Projects
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {pagination?.total || projects.length} projects
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenNewProject}
        >
          New Project
        </Button>
      </Box>

      {/* Filters */}
      <ProjectFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        clients={clients}
      />

      {/* Projects Grid */}
      {isLoading ? (
        <Typography>Loading...</Typography>
      ) : projects.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <Grid container spacing={3}>
            {projects.map((project) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={project._id}>
                <ProjectCard
                  project={project}
                  onEdit={handleEditProject}
                  onDelete={handleDeleteClick}
                />
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      {/* Project Detail Modal */}
      <ProjectDetailModal
        open={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        project={selectedProject}
        onSave={handleSaveProject}
        isLoading={mutations.createProject.isPending || mutations.updateProject.isPending}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!projectToDelete}
        title="Delete Project"
        message={`Are you sure you want to delete "${projectToDelete?.name}"? Tasks and notes will be unlinked but not deleted.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setProjectToDelete(null)}
        loading={mutations.deleteProject.isPending}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectsPage;
